import { CircleNotchIcon as Spinner, MonitorIcon as Monitor } from "@phosphor-icons/react";
import BindingStatusBadge from "@/components/screenbindings/BindingStatusBadge";
import OriginBadge from "@/components/screenbindings/OriginBadge";
import { isInactiveBinding, isManualBinding, operationLabel, OPERATION_META } from "@/lib/screenBindings";

const TH = "px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Iter 45 — table of observed screen format bindings. */
export default function BindingTable({ page, loading, selectedId, onSelect, onPage }) {
  if (loading && !page) {
    return <div className="ml-card p-8 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]" data-testid="sb-loading"><Spinner size={18} className="animate-spin mr-2" /> Loading screen format bindings…</div>;
  }
  if (!page) return null;
  if (page.items.length === 0) {
    return (
      <div className="ml-card p-8 text-center" data-testid="sb-empty">
        <Monitor size={28} className="mx-auto mb-2 text-[var(--ml-text-secondary)]" />
        <p className="text-sm text-[var(--ml-text-secondary)]">
          {page.total === 0 && !Object.values(page.filters || {}).some(Boolean)
            ? "No observed screen format bindings yet. Parse RPG programs and their display-file DDS, then rebuild."
            : "No bindings match these filters."}
        </p>
      </div>
    );
  }
  return (
    <div className="ml-card overflow-hidden" data-testid="sb-table" data-count={page.items.length}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--ml-surface)] border-b border-[var(--ml-border)]">
            <tr>
              <th className={TH}>Program</th>
              <th className={TH}>Display file</th>
              <th className={TH}>Record format</th>
              <th className={TH}>Operation</th>
              <th className={TH}>Origin</th>
              <th className={TH}>Status · confidence</th>
              <th className={TH}>Source line</th>
              <th className={TH}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((b, i) => (
              <tr
                key={b.id}
                onClick={() => onSelect(b.id)}
                data-testid={`sb-row-${b.id}`}
                data-status={b.status}
                data-confidence={b.confidence}
                data-operation={b.operation}
                data-origin={isManualBinding(b) ? "manual" : "parser"}
                data-active={isInactiveBinding(b) ? "false" : "true"}
                className={`border-b border-[var(--ml-border)] last:border-b-0 cursor-pointer hover:bg-[var(--ml-primary)]/5 ${
                  selectedId === b.id ? "bg-[var(--ml-primary)]/8" : isManualBinding(b) ? "bg-[#fff1f1]/40" : i % 2 ? "bg-[var(--ml-surface)]/30" : ""
                } ${isInactiveBinding(b) ? "opacity-60 line-through decoration-[var(--ml-border)]" : ""}`}
              >
                <td className="px-3 py-2 ml-mono font-semibold whitespace-nowrap">
                  {b.program_library && <span className="font-normal text-[var(--ml-text-secondary)]">{b.program_library}/</span>}{b.program_name}
                </td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap" data-testid={`sb-display-${b.id}`}>
                  {b.display_file || (
                    <span className={b.status === "ambiguous" ? "text-[#8a6d00]" : "text-[var(--ml-text-secondary)]"} title={b.ambiguity_reason || ""}>
                      {b.status === "ambiguous" ? "ambiguous format name" : "unresolved format usage"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap">{b.record_format}</td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap" title={(OPERATION_META[b.operation] || {}).hint}>{operationLabel(b.operation)}</td>
                <td className="px-3 py-2 whitespace-nowrap"><OriginBadge origin={isManualBinding(b) ? "manual" : "parser"} inactive={isInactiveBinding(b)} compact testid={`sb-origin-${b.id}`} /></td>
                <td className="px-3 py-2 whitespace-nowrap"><BindingStatusBadge status={b.status} confidence={b.confidence} testid={`sb-status-${b.id}`} /></td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap" data-testid={`sb-line-${b.id}`}>
                  {b.source_line ?? (isManualBinding(b) ? <span className="text-[var(--ml-text-secondary)]" title="Consultant supplied — no source statement is claimed">consultant supplied</span> : "—")}
                </td>
                <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap text-[var(--ml-text-secondary)]">{fmtDate(b.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-3 py-2 border-t border-[var(--ml-border)] text-xs text-[var(--ml-text-secondary)]">
        <span data-testid="sb-page-info">{page.offset + 1}–{page.offset + page.items.length} of {page.total}</span>
        <span className="flex gap-2">
          <button type="button" disabled={page.offset === 0} onClick={() => onPage(Math.max(0, page.offset - page.limit))} className="ml-mono text-[10px] uppercase tracking-widest disabled:opacity-40 hover:underline" data-testid="sb-page-prev">Prev</button>
          <button type="button" disabled={page.offset + page.limit >= page.total} onClick={() => onPage(page.offset + page.limit)} className="ml-mono text-[10px] uppercase tracking-widest disabled:opacity-40 hover:underline" data-testid="sb-page-next">Next</button>
        </span>
      </div>
    </div>
  );
}
